/**
 * A ajuda contextual do Portal (D-203, DS-698): um ícone discreto ao lado do título do grupo e uma bolha com a
 * explicação. É a `DicaContextual` do app interno (`apps/web/src/componentes/dica-contextual.tsx`), com o mesmo
 * comportamento, no CSS próprio do portal.
 *
 * ## Por que um componente do portal, e não o do app interno
 *
 * O portal é outra SPA, com outra folha e sem Tailwind (DS-520). A dica do app interno se apoia no
 * `PainelFlutuante`, na pilha da `Sobreposicao` e nas classes utilitárias, e nada disso existe aqui. A regra é a
 * mesma; o componente é de cada SPA, como o seletor com capa (`capa.tsx`). O ⓘ "Como ler esta página"
 * (`como-ler.tsx`) continua sendo a ajuda da PÁGINA; esta é a do grupo.
 *
 * ## O texto mora num nó só
 *
 * Fechada, a dica deixa o texto num `<span hidden>`; aberta, a bolha carrega o MESMO id. O `aria-describedby` do
 * botão aponta para esse id nos dois estados: quem usa leitor de tela ouve a explicação ao focar o ícone, e o texto
 * nunca existe duas vezes no DOM. Sem `role` de tooltip, diálogo ou status: a bolha é descrição, não região viva.
 *
 * ## Onde a bolha fica
 *
 * Presa à janela (`position: fixed`), medida pelo `getBoundingClientRect` do ícone: abre embaixo, vira para cima
 * quando não cabe e nunca passa das bordas, e acompanha a rolagem (inclusive a do corpo do modal, ouvida em
 * captura). Ela fica DENTRO da caixa do ícone, sem portal: `fixed` escapa do `overflow: hidden` do `.painel` e do
 * `overflow: auto` do `.modal`, e nenhum ancestral das telas tem `transform` (o `ModalDoPortal` não tem). Portada
 * para o `body`, ela nasceria inerte debaixo de um diálogo aberto, que marca `inert` os irmãos do véu.
 *
 * ## Como abre e fecha
 *
 * - Mouse (`hover: hover` e `pointer: fine`): parar 400 ms sobre o ícone abre uma prévia, que fica enquanto o
 *   ponteiro estiver no ícone ou na bolha (200 ms para atravessar o vão). O clique fixa.
 * - Toque: o toque abre e fecha; não depende de hover.
 * - Teclado: Enter ou Espaço abre e fecha, Esc fecha e deixa o foco no ícone, Tab para fora fecha. O foco sozinho
 *   não abre (WCAG 3.2.1).
 * - O Esc é consumido em CAPTURA e não segue adiante: o `ModalDoPortal` escuta o Esc em bolha no `document`, e
 *   dentro do modal o primeiro Esc fecha só a dica. Debaixo de um diálogo (o ícone `inert`), o Esc é do diálogo.
 * - Clique ou toque fora, e outra dica que abre, fecham. Uma aberta por vez.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Info } from 'lucide-react'

/** Espera do mouse parado antes da prévia: passar por cima a caminho de outro lugar não abre nada. */
export const ESPERA_DA_PREVIA_MS = 400
/** Tolerância para o ponteiro atravessar o vão entre o ícone e a bolha (WCAG 1.4.13). */
export const TOLERANCIA_DE_SAIDA_MS = 200
/** Largura máxima da bolha; a geometria ainda a prende à janela. */
const LARGURA_MAXIMA = 288
const LARGURA_MINIMA = 176
const ALTURA_DESEJADA = 220
/** Respiro entre o ícone e a bolha. */
const FOLGA = 6
/** Margem mínima até a borda da janela. */
const MARGEM = 8
/** Quanto o lado de cima precisa ser MELHOR para valer a virada: sem folga, a bolha pularia de lado a cada pixel. */
const GANHO_MINIMO_PARA_VIRAR = 24
const COM_MOUSE = '(hover: hover) and (pointer: fine)'

/**
 * Uma dica aberta por vez, no portal inteiro: abrir uma fecha a anterior. Um registro de módulo, porque as dicas não
 * se conhecem.
 */
let fecharADicaAberta: (() => void) | null = null

type Estado = 'fechada' | 'previa' | 'fixa'

interface Geometria {
  readonly left: number
  /** A borda de cima, quando abre embaixo. */
  readonly top: number | null
  /** A borda de baixo, quando abre em cima: a bolha fica colada ao ícone, qualquer que seja a altura do texto. */
  readonly bottom: number | null
  readonly width: number
  readonly maxHeight: number
  readonly acima: boolean
}

function useComMouse(): boolean {
  const [combina, setCombina] = useState(() => window.matchMedia(COM_MOUSE).matches)
  useEffect(() => {
    const consulta = window.matchMedia(COM_MOUSE)
    const aoMudar = (): void => {
      setCombina(consulta.matches)
    }
    consulta.addEventListener('change', aoMudar)
    return () => {
      consulta.removeEventListener('change', aoMudar)
    }
  }, [])
  return combina
}

/** Largura pela quantidade de texto: uma frase curta não precisa de uma bolha de 288 px. */
function larguraPeloTexto(conteudo: ReactNode): number {
  if (typeof conteudo !== 'string') return LARGURA_MAXIMA
  return Math.max(LARGURA_MINIMA, Math.min(LARGURA_MAXIMA, Math.round(conteudo.length * 6.4) + 28))
}

/** Onde a bolha cabe, em coordenadas da janela (a mesma conta do `PainelFlutuante` do app interno). */
function geometriaPara(ancora: DOMRect, largura: number, alinhar: 'inicio' | 'fim'): Geometria {
  const alturaDaJanela = window.innerHeight
  const larguraDaJanela = window.innerWidth
  const espacoAbaixo = alturaDaJanela - MARGEM - ancora.bottom - FOLGA
  const espacoAcima = ancora.top - MARGEM - FOLGA
  // Vira para cima quando NÃO CABE embaixo e em cima sobra mais.
  const acima = espacoAbaixo < ALTURA_DESEJADA && espacoAcima > espacoAbaixo + GANHO_MINIMO_PARA_VIRAR
  const maxHeight = Math.max(0, Math.min(ALTURA_DESEJADA, acima ? espacoAcima : espacoAbaixo))
  const width = Math.min(largura, larguraDaJanela - MARGEM * 2)
  const bruto = alinhar === 'fim' ? ancora.right - width : ancora.left
  const left = Math.max(MARGEM, Math.min(bruto, larguraDaJanela - width - MARGEM))
  return {
    left,
    top: acima ? null : ancora.bottom + FOLGA,
    bottom: acima ? alturaDaJanela - ancora.top + FOLGA : null,
    width,
    maxHeight,
    acima,
  }
}

export function DicaDoPortal({
  rotulo,
  children,
  alinhar = 'inicio',
  teste,
}: {
  /** De que é a dica: o título do grupo. Vira "Sobre {rotulo}". */
  rotulo: string
  /** Uma a três frases. Texto, sem link nem controle: a bolha não recebe foco. */
  children: ReactNode
  /** `fim` quando o ícone fica na borda direita: a bolha cresce para a esquerda. */
  alinhar?: 'inicio' | 'fim'
  teste?: string
}) {
  const idDoTexto = `${useId()}-dica`
  const [estado, setEstado] = useState<Estado>('fechada')
  const aberta = estado !== 'fechada'
  const [geometria, setGeometria] = useState<Geometria | null>(null)
  const botao = useRef<HTMLButtonElement>(null)
  const caixa = useRef<HTMLSpanElement>(null)
  const temporizador = useRef<number | undefined>(undefined)
  const comMouse = useComMouse()
  const largura = larguraPeloTexto(children)

  const limparTemporizador = (): void => {
    window.clearTimeout(temporizador.current)
    temporizador.current = undefined
  }

  const fechar = useCallback((): void => {
    window.clearTimeout(temporizador.current)
    temporizador.current = undefined
    setEstado('fechada')
  }, [])

  const abrir = (como: 'previa' | 'fixa'): void => {
    if (fecharADicaAberta !== null && fecharADicaAberta !== fechar) fecharADicaAberta()
    fecharADicaAberta = fechar
    setEstado(como)
  }

  // A dica que sai da tela deixa de ser "a aberta".
  useEffect(() => () => {
    window.clearTimeout(temporizador.current)
    if (fecharADicaAberta === fechar) fecharADicaAberta = null
  }, [fechar])

  const medir = useCallback((): void => {
    const ancora = botao.current?.getBoundingClientRect()
    if (ancora === undefined) return
    const nova = geometriaPara(ancora, largura, alinhar)
    setGeometria((anterior) =>
      anterior !== null
      && anterior.left === nova.left
      && anterior.top === nova.top
      && anterior.bottom === nova.bottom
      && anterior.width === nova.width
      && anterior.maxHeight === nova.maxHeight
        ? anterior
        : nova,
    )
  }, [largura, alinhar])

  /*
    `useLayoutEffect` para a primeira medida: com `useEffect` a bolha pintaria uma vez fora do lugar. A rolagem é
    ouvida em CAPTURA, porque o corpo do modal rola por dentro e o `window` sozinho não veria.
  */
  useLayoutEffect(() => {
    if (!aberta) return undefined
    medir()
    const aoMover = (): void => {
      medir()
    }
    window.addEventListener('resize', aoMover)
    window.addEventListener('scroll', aoMover, { capture: true, passive: true })
    return () => {
      window.removeEventListener('resize', aoMover)
      window.removeEventListener('scroll', aoMover, { capture: true })
    }
  }, [aberta, medir])

  // Esc em captura (antes do modal) e clique ou toque fora do conjunto ícone + bolha.
  useEffect(() => {
    if (!aberta) return undefined
    const noEscape = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return
      // Outro componente já tratou este Esc: ele é dele.
      if (evento.defaultPrevented) return
      // Debaixo de um diálogo, o Esc é dele.
      if (botao.current?.closest('[inert]') != null) return
      evento.preventDefault()
      evento.stopPropagation()
      // O foco só volta ao ícone se estava nele (ou perdido): a prévia do mouse abre sem foco.
      const focoNoIcone = document.activeElement === botao.current || document.activeElement === document.body
      fechar()
      if (focoNoIcone) botao.current?.focus()
    }
    const foraDaDica = (evento: MouseEvent | TouchEvent): void => {
      if (evento.target instanceof Node && caixa.current?.contains(evento.target) === true) return
      fechar()
    }
    // O foco que vai para outro lugar fecha a dica. É assim que ela fecha quando um modal do portal
    // abre por cima (o portal não tem pilha de diálogos): o modal leva o foco para dentro dele.
    const focoFora = (evento: FocusEvent): void => {
      if (evento.target instanceof Node && caixa.current?.contains(evento.target) === true) return
      fechar()
    }
    document.addEventListener('keydown', noEscape, true)
    document.addEventListener('mousedown', foraDaDica)
    document.addEventListener('touchstart', foraDaDica, { passive: true })
    document.addEventListener('focusin', focoFora)
    return () => {
      document.removeEventListener('keydown', noEscape, true)
      document.removeEventListener('mousedown', foraDaDica)
      document.removeEventListener('touchstart', foraDaDica)
      document.removeEventListener('focusin', focoFora)
    }
  }, [aberta, fechar])

  const aoEntrar = (): void => {
    if (!comMouse) return
    limparTemporizador()
    if (aberta) return
    temporizador.current = window.setTimeout(() => {
      abrir('previa')
    }, ESPERA_DA_PREVIA_MS)
  }
  const aoSair = (): void => {
    if (!comMouse) return
    limparTemporizador()
    if (estado !== 'previa') return
    temporizador.current = window.setTimeout(fechar, TOLERANCIA_DE_SAIDA_MS)
  }

  return (
    <span ref={caixa} className="dica-do-portal">
      <button
        ref={botao}
        type="button"
        className="dica-do-portal-botao"
        aria-label={`Sobre ${rotulo}`}
        aria-expanded={aberta}
        aria-controls={aberta ? idDoTexto : undefined}
        aria-describedby={idDoTexto}
        data-teste={teste ?? 'dica-contextual'}
        onClick={() => {
          limparTemporizador()
          if (estado === 'fixa') fechar()
          else abrir('fixa')
        }}
        onMouseEnter={aoEntrar}
        onMouseLeave={aoSair}
        onBlur={(evento) => {
          // O foco foi para OUTRO controle (Tab, ou clique num campo): fecha. Clicar no texto da bolha não leva o
          // foco a lugar nenhum (`relatedTarget` nulo), e quem decide esse caso é o clique fora.
          const para = evento.relatedTarget
          if (para === null) return
          if (caixa.current?.contains(para) === true) return
          fechar()
        }}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {aberta ? (
        <span
          id={idDoTexto}
          className="dica-do-portal-bolha"
          data-teste="dica-contextual-bolha"
          data-acima={geometria?.acima === true ? '' : undefined}
          style={
            geometria === null
              ? { visibility: 'hidden' }
              : {
                  left: geometria.left,
                  top: geometria.top ?? undefined,
                  bottom: geometria.bottom ?? undefined,
                  width: geometria.width,
                  maxHeight: geometria.maxHeight,
                }
          }
          onMouseEnter={limparTemporizador}
          onMouseLeave={aoSair}
        >
          {children}
        </span>
      ) : (
        <span id={idDoTexto} hidden>
          {children}
        </span>
      )}
    </span>
  )
}
